import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useDeviceSessionTimeline, type EventRow } from '../api/hooks';
import {
  fmtAgo,
  fmtDuration,
  fmtMs,
  ulidTimestampMs,
} from '../components/format';
import { KPITile, BreakdownTable } from '../components/kpis';
import { SessionLocationCard } from '../components/SessionLocationCard';
import { buildRawRows, type RawRow } from '../components/sessionRawRows';
import {
  aggregateContent,
  aggregateProducts,
  aggregateSourceSwitches,
  aggregateViews,
  findErrors,
  findFeedbackRatings,
  type ProductSessionAgg,
  type ViewAgg,
  type ContentPlay,
} from '../components/sessionAggregates';

// Column-header help text. Surfaced via native `title` so we don't pull
// in a tooltip lib; matches the deltaHint idiom on KPITile.
const PRODUCT_COL_HINTS = {
  impressions:
    'Times this product was on-screen in the carousel for ≥ 2 s (a deliberate look). Brief sweeps are excluded.',
  detail_views: "Times the user opened this product's detail view.",
  avg_dwell: 'Average time spent on the detail view per visit.',
  popups: "Feature popup opens (the spec / 'tell me more' dialog).",
  gallery: 'Product image gallery opens.',
} as const;

// Event types the user explicitly does NOT want to see on this page,
// neither in Overview's derived sections nor in Raw's chronological
// list. They're projector-state context that's noise here — the device
// timeline on the parent page already covers them.
const RAW_HIDDEN_TYPES = new Set([
  'state_change',
  'demo_mode_change',
  'boot',
  'limiter_active',
]);

// ─── Offset / time formatters ──────────────────────────────────────────

function fmtOffset(deltaMs: number): string {
  const s = Math.max(0, Math.round(deltaMs / 1000));
  if (s < 60) return `+0:${String(s).padStart(2, '0')}`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return `+${m}:${String(sec).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `+${h}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function railColour(type: string): string {
  switch (type) {
    case 'view_visit':       return 'bg-accent';
    case 'content_play':     return 'bg-blue-500';
    case 'feedback':         return 'bg-emerald-500';
    case 'demo_session':     return 'bg-fg';
    case 'source_dwell':     return 'bg-slate-400';
    case 'product_view':     return 'bg-cyan-500';
    case 'feature_view':     return 'bg-violet-500';
    case 'gallery_view':     return 'bg-pink-500';
    case 'compare_view':     return 'bg-orange-500';
    case 'error':            return 'bg-warn';
    default:                 return 'bg-muted';
  }
}

// Raw-timeline row math (getDwellMs / buildRawRows / RawRow) lives in
// ../components/sessionRawRows so it can be unit-tested without this page's
// Leaflet-bearing import graph (DE-168).

// Primary noun of the event — the "what was happening" cell.
function subjectFor(ev: EventRow): React.ReactNode {
  const d = (ev.data ?? {}) as Record<string, unknown>;
  switch (ev.event_type) {
    case 'view_visit':
      return String(d.view ?? '?');
    case 'content_play':
      return <span className="font-mono">{String(d.id ?? '?')}</span>;
    case 'product_view':
    case 'feature_view':
    case 'gallery_view':
      return String(d.product_model ?? '?');
    case 'compare_view':
      return 'Compare table';
    case 'source_dwell':
      return (
        <span className="font-mono">
          {String(d.from ?? '?')} → {String(d.to ?? '?')}
        </span>
      );
    default:
      return null;
  }
}

// Secondary info — kept short; key counts, source kind, label, ratings.
function detailFor(ev: EventRow): React.ReactNode {
  const d = (ev.data ?? {}) as Record<string, unknown>;
  switch (ev.event_type) {
    case 'view_visit': {
      const keys = d.keys as Record<string, unknown> | undefined;
      if (!keys) return null;
      const parts = Object.entries(keys)
        .map(([k, v]) => {
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? `${k}×${n}` : null;
        })
        .filter((s): s is string => s !== null);
      return parts.length > 0
        ? <span className="font-mono text-[11px]">{parts.join(' ')}</span>
        : null;
    }
    case 'content_play': {
      const kind = String(d.kind ?? 'video');
      const done = d.completed === true;
      return `${kind} · ${done ? '✓' : 'incomplete'}`;
    }
    case 'product_view':
      return String(d.source ?? '');
    case 'feature_view':
      return String(d.pill_label ?? d.pill_id ?? '');
    case 'gallery_view':
      return `image #${d.gallery_index ?? '?'}`;
    case 'source_dwell': {
      // DE-168: the switch is a point event, so the Duration column reads
      // "—"; surface the prior source's dwell here instead.
      const prev = String(d.from ?? '?');
      const secs = Number(d.dwell_s ?? 0);
      return secs > 0 ? `${fmtDuration(secs)} on ${prev}` : null;
    }
    case 'feedback': {
      const ratings = d.ratings;
      if (Array.isArray(ratings) && ratings.length > 0) {
        return <span className="font-mono">ratings {ratings.join(' ')}</span>;
      }
      return 'submitted';
    }
    case 'demo_session': {
      const active = Number(d.active_s ?? 0);
      const susp = Number(d.suspended_s ?? 0);
      const views = d.view_count ?? 0;
      const plays = d.content_play_count ?? 0;
      const reached = d.reached_feedback === true;
      const parts: string[] = [];
      if (active > 0) parts.push(`active ${fmtDuration(active)}`);
      if (susp > 0) parts.push(`suspended ${fmtDuration(susp)}`);
      parts.push(`${views} views`);
      parts.push(`${plays} plays`);
      parts.push(reached ? '✓ feedback' : '— feedback');
      return parts.join(' · ');
    }
    case 'error':
      return (
        <span className="text-warn">
          {String(d.message ?? d.code ?? 'error')}
        </span>
      );
    default:
      return null;
  }
}

// ─── Page ──────────────────────────────────────────────────────────────

type Tab = 'overview' | 'raw';

function useTab(): { tab: Tab; setTab: (t: Tab) => void } {
  const [params, setParams] = useSearchParams();
  const raw = params.get('view');
  const tab: Tab = raw === 'raw' ? 'raw' : 'overview';
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'overview') next.delete('view');
    else next.set('view', t);
    setParams(next, { replace: true });
  };
  return { tab, setTab };
}

export function DeviceSessionTimeline() {
  const { id, sessionId } = useParams();
  const timeline = useDeviceSessionTimeline(id, sessionId);
  const { tab, setTab } = useTab();

  const ulidStart = sessionId ? ulidTimestampMs(sessionId) : null;
  const backendStart = timeline.data ? new Date(timeline.data.start).getTime() : null;
  const startMs = ulidStart ?? backendStart;
  const endMs = timeline.data ? new Date(timeline.data.end).getTime() : null;
  const session = timeline.data?.session;
  const events = timeline.data?.events ?? [];

  const products = useMemo(
    () => aggregateProducts(events, startMs ?? 0),
    [events, startMs],
  );
  const views = useMemo(() => aggregateViews(events), [events]);
  const content = useMemo(() => aggregateContent(events), [events]);
  const sourceSwitches = useMemo(
    () => (startMs !== null ? aggregateSourceSwitches(events, startMs) : []),
    [events, startMs],
  );
  const feedbackRatings = useMemo(() => findFeedbackRatings(events), [events]);
  const errors = useMemo(
    () =>
      startMs !== null && endMs !== null
        ? findErrors(events, startMs, endMs)
        : [],
    [events, startMs, endMs],
  );

  const activeS = Number(session?.data?.active_s ?? 0);
  const suspendedS = Number(session?.data?.suspended_s ?? 0);
  const totalS = activeS + suspendedS;
  const viewCount = Number(session?.data?.view_count ?? 0);
  const playCount = Number(session?.data?.content_play_count ?? 0);
  const reachedFeedback = session?.data?.reached_feedback === true;

  // Status pill — surface whether the device closed the session cleanly,
  // whether feedback was reached, or whether we never received a closing
  // demo_session row at all (might mean the device crashed mid-session).
  const statusPill = (() => {
    if (!session) {
      return { label: 'No closing event', cls: 'bg-warn/10 text-warn' };
    }
    if (reachedFeedback) {
      return { label: 'Completed', cls: 'bg-accent/10 text-accent' };
    }
    return { label: 'Ended without feedback', cls: 'bg-soft text-muted' };
  })();

  const startISO = startMs ? new Date(startMs).toISOString() : null;
  const endISO = endMs ? new Date(endMs).toISOString() : null;

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div>
        <Link
          to={`/devices/${encodeURIComponent(id ?? '')}`}
          className="text-xs text-accent hover:underline"
        >
          ← back to device
        </Link>
        <div className="flex items-baseline gap-3 mt-1 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight">Demo session</h1>
          <span
            className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${statusPill.cls}`}
          >
            {statusPill.label}
          </span>
        </div>
        <p className="text-xs text-muted font-mono break-all mt-0.5">
          {id} · {sessionId}
        </p>
        {startMs && (
          <p className="text-xs text-muted mt-1">
            started {fmtAgo(new Date(startMs).toISOString())} —{' '}
            <span className="tabular-nums">
              {new Date(startMs).toLocaleString()}
            </span>
            {endMs && (
              <>
                {' '}
                · ended{' '}
                <span className="tabular-nums">
                  {new Date(endMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </>
            )}
          </p>
        )}

        {/* Tabs */}
        <div className="mt-3 flex gap-1 border-b border-border">
          {(['overview', 'raw'] as const).map((t) => {
            const active = tab === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs rounded-t-md border-b-2 -mb-px ${
                  active
                    ? 'border-accent text-fg font-medium'
                    : 'border-transparent text-muted hover:text-fg'
                }`}
              >
                {t === 'overview' ? 'Overview' : 'Raw timeline'}
              </button>
            );
          })}
        </div>
      </div>

      {timeline.isLoading && (
        <p className="text-sm text-muted card">Loading…</p>
      )}
      {timeline.error && (
        <p className="text-sm text-warn card">
          {(timeline.error as Error).message}
        </p>
      )}

      {timeline.data && tab === 'overview' && (
        <Overview
          thing={id ?? ''}
          startISO={startISO}
          endISO={endISO}
          totalS={totalS}
          activeS={activeS}
          suspendedS={suspendedS}
          viewCount={viewCount}
          playCount={playCount}
          reachedFeedback={reachedFeedback}
          feedbackRatings={feedbackRatings}
          products={products}
          views={views}
          content={content}
          sourceSwitches={sourceSwitches}
          errors={errors}
          startMs={startMs}
        />
      )}

      {timeline.data && tab === 'raw' && (
        <RawTimeline events={events} startMs={startMs} />
      )}
    </div>
  );
}

// ─── Overview composition ──────────────────────────────────────────────

function Overview({
  thing,
  startISO,
  endISO,
  totalS,
  activeS,
  suspendedS,
  viewCount,
  playCount,
  reachedFeedback,
  feedbackRatings,
  products,
  views,
  content,
  sourceSwitches,
  errors,
  startMs,
}: {
  thing: string;
  startISO: string | null;
  endISO: string | null;
  totalS: number;
  activeS: number;
  suspendedS: number;
  viewCount: number;
  playCount: number;
  reachedFeedback: boolean;
  feedbackRatings: number[] | null;
  products: ReturnType<typeof aggregateProducts>;
  views: ViewAgg[];
  content: ReturnType<typeof aggregateContent>;
  sourceSwitches: ReturnType<typeof aggregateSourceSwitches>;
  errors: ReturnType<typeof findErrors>;
  startMs: number | null;
}) {
  const durationHint =
    activeS > 0 && suspendedS > 0
      ? `active ${fmtDuration(activeS)} · suspended ${fmtDuration(suspendedS)}`
      : undefined;
  const feedbackValue = reachedFeedback ? '✓' : '—';
  const feedbackHint = feedbackRatings
    ? `ratings ${feedbackRatings.join(' ')}`
    : undefined;

  return (
    <>
      {/* KPI band — bordered tint matching DemoExperience's band. */}
      <div className="rounded-lg border border-border bg-soft/40 overflow-hidden">
        <div className="flex divide-x divide-border">
          <KPITile
            label="Duration"
            value={fmtDuration(totalS)}
            hint={durationHint}
          />
          <KPITile
            label="Views / Plays"
            value={`${viewCount} · ${playCount}`}
          />
          <KPITile
            label="Feedback"
            value={feedbackValue}
            hint={feedbackHint}
          />
        </div>
      </div>

      {/* Location card */}
      {thing && startISO && endISO && (
        <SessionLocationCard
          thing={thing}
          startISO={startISO}
          endISO={endISO}
        />
      )}

      {/* Product engagement */}
      {(products.perProduct.length > 0 || products.compare.views > 0) && (
        <section className="card">
          <h2 className="font-medium mb-3">Product engagement</h2>
          {products.perProduct.length === 0 ? (
            <p className="text-xs text-muted">
              No per-product engagement recorded.
            </p>
          ) : (
            <ProductEngagementTable products={products.perProduct} />
          )}
          {products.compare.views > 0 && (
            <div className="mt-2 pt-2 border-t border-border text-xs text-muted flex justify-between">
              <span>Compare table</span>
              <span className="tabular-nums">
                {products.compare.views} view
                {products.compare.views === 1 ? '' : 's'} ·{' '}
                {fmtMs(products.compare.total_dwell_ms)} total dwell
              </span>
            </div>
          )}
        </section>
      )}

      {/* Content played */}
      {content.plays.length > 0 && (
        <section className="card">
          <h2 className="font-medium mb-3">Content played</h2>
          <BreakdownTable<ContentPlay>
            title=""
            rows={content.plays}
            rowKey={(r) => r.event_id}
            emptyText="—"
            columns={[
              {
                header: 'kind',
                headerTitle: 'video or audio.',
                render: (r) => r.kind,
              },
              {
                header: 'id',
                headerTitle: 'Content identifier — video / audio asset id.',
                render: (r) => r.id,
                mono: true,
                maxChars: 32,
              },
              {
                header: 'watched',
                headerTitle:
                  'Time the user spent watching this play before exiting.',
                align: 'right',
                render: (r) => fmtDuration(r.watched_ms / 1000),
              },
              {
                header: 'completed',
                headerTitle:
                  "Did the play reach the content's end? ✓ = watched_ms == full duration.",
                align: 'right',
                render: (r) => (r.completed ? '✓' : '—'),
              },
            ]}
          />
          <div className="mt-2 pt-2 border-t border-border text-xs text-muted flex justify-between">
            <span>
              {content.plays.length} play
              {content.plays.length === 1 ? '' : 's'}
            </span>
            <span className="tabular-nums">
              {fmtDuration(content.totalWatchedMs / 1000)} total watched
            </span>
          </div>
        </section>
      )}

      {/* View visits */}
      {views.length > 0 && (
        <section className="card">
          <h2 className="font-medium mb-3">View visits</h2>
          <BreakdownTable<ViewAgg>
            title=""
            rows={views}
            rowKey={(r) => r.view}
            emptyText="—"
            columns={[
              {
                header: 'view',
                headerTitle: 'QML view name (Main menu, Product page, …).',
                render: (r) => r.view,
                maxChars: 32,
              },
              {
                header: 'visits',
                headerTitle:
                  'Number of times this view was entered during the session.',
                align: 'right',
                render: (r) => r.visits,
              },
              {
                header: 'dwell',
                headerTitle:
                  'Cumulative time spent on this view across every visit.',
                align: 'right',
                render: (r) =>
                  r.total_dwell_ms
                    ? fmtDuration(r.total_dwell_ms / 1000)
                    : '—',
              },
              {
                header: 'keys',
                headerTitle:
                  'Remote keys pressed while in this view, with their counts.',
                align: 'right',
                render: (r) => r.keys_summary || '—',
                maxChars: 24,
              },
            ]}
          />
        </section>
      )}

      {/* Feedback ratings */}
      {feedbackRatings && feedbackRatings.length > 0 && (
        <section className="card">
          <h2 className="font-medium mb-3">Feedback</h2>
          <div className="flex flex-wrap gap-2">
            {feedbackRatings.map((r, i) => {
              const clamped = Math.max(1, Math.min(5, Math.round(r)));
              const tint =
                clamped <= 2
                  ? 'bg-warn/15 text-warn'
                  : clamped >= 4
                    ? 'bg-accent/15 text-accent'
                    : 'bg-soft text-muted';
              return (
                <span
                  key={i}
                  className={`text-xs px-2 py-1 rounded font-mono tabular-nums ${tint}`}
                >
                  {clamped} ★
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Errors during session */}
      {errors.length > 0 && (
        <section className="card">
          <h2 className="font-medium mb-3">Errors during session</h2>
          <ul className="space-y-1 text-xs">
            {errors.map((e) => (
              <li key={e.event_id} className="flex items-baseline gap-3">
                <span className="text-muted tabular-nums w-16 shrink-0">
                  {fmtOffset(e.offset_ms)}
                </span>
                <span className="text-warn">{e.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Source switches */}
      {sourceSwitches.length > 0 && startMs !== null && (
        <section className="card">
          <h2 className="font-medium mb-3">Source switches</h2>
          <p className="text-xs text-muted flex flex-wrap items-baseline gap-1.5">
            {sourceSwitches.map((s, i) => (
              <span key={s.ts} className="flex items-baseline gap-1.5">
                {i === 0 && s.from && (
                  <span className="font-mono">{s.from}</span>
                )}
                <span aria-hidden="true">→</span>
                <span className="font-mono">{s.to}</span>
                <span className="text-[10px]">
                  ({fmtOffset(s.offset_ms)})
                </span>
              </span>
            ))}
          </p>
        </section>
      )}
    </>
  );
}

// ─── Raw timeline ──────────────────────────────────────────────────────

function RawTimeline({
  events,
  startMs,
}: {
  events: EventRow[];
  startMs: number | null;
}) {
  // Filter out state events even from the raw view — per user, state
  // context belongs on the device timeline, not on a session deep-dive.
  const filtered = useMemo(
    () => events.filter((ev) => !RAW_HIDDEN_TYPES.has(ev.event_type)),
    [events],
  );

  // Compute per-row windows + nesting depth + sorted order.
  const rows = useMemo(
    () => buildRawRows(filtered, startMs ?? 0),
    [filtered, startMs],
  );

  // Group by start minute so the chronological list reads as paragraphs
  // of activity rather than one long ladder. Grouping by *start* (not
  // emission) anchors a long view_visit in the minute it actually began.
  const groups = useMemo(() => {
    const map = new Map<number, RawRow[]>();
    for (const r of rows) {
      const m = Math.floor(r.start_ms / 60_000);
      let arr = map.get(m);
      if (!arr) {
        arr = [];
        map.set(m, arr);
      }
      arr.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [rows]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted card">
        No session-relevant events recorded.
      </p>
    );
  }

  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-muted bg-soft/40 border-b border-border">
            <th
              className="text-left font-medium py-1.5 px-4 cursor-help"
              title="When the activity began, in seconds within the wall-clock minute shown in the section header (so :41 under the 16:25 header means 16:25:41). Most events fire on exit and carry a dwell — Started = emission − dwell, so the second matches when the user actually entered the view / focused the card."
              style={{ width: '64px' }}
            >
              Started
            </th>
            <th
              className="text-right font-medium py-1.5 pr-3 cursor-help"
              title="How long the activity lasted on the device until the user moved on. '—' for point events with no dwell."
              style={{ width: '80px' }}
            >
              Duration
            </th>
            <th
              className="text-left font-medium py-1.5 pr-3 cursor-help"
              title="Event type. The colour dot matches the legend on the device state timeline."
              style={{ width: '140px' }}
            >
              Event
            </th>
            <th className="text-left font-medium py-1.5 pr-3">Subject</th>
            <th className="text-left font-medium py-1.5 pr-4">Detail</th>
          </tr>
        </thead>
        {groups.map(([minuteKey, group], gi) => {
          const minuteTs = minuteKey * 60_000;
          return (
            <tbody
              key={minuteKey}
              className={gi > 0 ? 'border-t-2 border-border' : ''}
            >
              <tr className="bg-soft/40">
                <td
                  colSpan={5}
                  className="px-4 py-1 text-[10px] uppercase tracking-widest text-muted tabular-nums"
                >
                  {new Date(minuteTs).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
              </tr>
              {group.map((r) => {
                const ev = r.ev;
                const startedAbs = new Date(r.start_ms).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                });
                const endedAbs = new Date(r.end_ms).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                });
                const offsetMs = r.start_ms - (startMs ?? r.start_ms);
                // Wall-clock seconds inside the minute header. Avoids
                // the "missing seconds" visual gap a session-relative
                // offset creates whenever the session starts mid-minute
                // (e.g. 16:25:41 → first group ran +0:00 → +0:18, then
                // 16:26 group started at +0:19, looking like a 40-second
                // hole between groups).
                const secInMinute = Math.floor(r.start_ms / 1000) % 60;
                const startedLabel = `:${String(secInMinute).padStart(2, '0')}`;
                const startedTitle =
                  r.duration_ms > 0
                    ? `started ${startedAbs} · ended ${endedAbs} · ${fmtOffset(offsetMs)} from session start`
                    : `${startedAbs} · ${fmtOffset(offsetMs)} from session start`;
                const durationLabel =
                  r.duration_ms > 0 ? fmtMs(r.duration_ms) : '—';
                const subject = subjectFor(ev);
                const detail = detailFor(ev);
                return (
                  <tr
                    key={ev.event_id}
                    className="border-b border-border last:border-0"
                  >
                    <td
                      className="py-1.5 px-4 text-muted tabular-nums align-top"
                      title={startedTitle}
                      style={{ paddingLeft: 16 + r.depth * 16 }}
                    >
                      {startedLabel}
                    </td>
                    <td className="py-1.5 pr-3 text-right text-muted tabular-nums align-top">
                      {durationLabel}
                    </td>
                    <td className="py-1.5 pr-3 align-top">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${railColour(ev.event_type)}`}
                          aria-hidden="true"
                        />
                        <span className="text-muted">{ev.event_type}</span>
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 align-top">
                      {subject ?? <span className="text-muted">—</span>}
                    </td>
                    <td className="py-1.5 pr-4 align-top">
                      {detail ?? <span className="text-muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

// ─── Product engagement (accordion) ────────────────────────────────────

/**
 * Each row is collapsed by default. Click → expand panel below with the
 * three rich event streams for the product (detail-view dwells, feature
 * popups, gallery opens). Single-select accordion to match the device
 * page's product table at DemoExperience.tsx:953.
 *
 * Column headers carry native `title` tooltips since several metric
 * names are jargon ("impressions", "popups") — the user shouldn't have
 * to dig through code to understand what each number counts.
 */
function ProductEngagementTable({
  products,
}: {
  products: ProductSessionAgg[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-muted">
          <th className="text-left pb-2 font-medium">product</th>
          <th
            className="text-right pb-2 font-medium cursor-help"
            title={PRODUCT_COL_HINTS.impressions}
          >
            impressions
          </th>
          <th
            className="text-right pb-2 font-medium cursor-help"
            title={PRODUCT_COL_HINTS.detail_views}
          >
            detail views
          </th>
          <th
            className="text-right pb-2 font-medium cursor-help"
            title={PRODUCT_COL_HINTS.avg_dwell}
          >
            avg dwell
          </th>
          <th
            className="text-right pb-2 font-medium cursor-help"
            title={PRODUCT_COL_HINTS.popups}
          >
            popups
          </th>
          <th
            className="text-right pb-2 font-medium cursor-help"
            title={PRODUCT_COL_HINTS.gallery}
          >
            gallery
          </th>
          <th className="pb-2 w-6" />
        </tr>
      </thead>
      <tbody>
        {products.map((p) => {
          const isOpen = expanded === p.product_model;
          return (
            <ProductRow
              key={p.product_model}
              product={p}
              isOpen={isOpen}
              onToggle={() =>
                setExpanded((cur) => (cur === p.product_model ? null : p.product_model))
              }
            />
          );
        })}
      </tbody>
    </table>
  );
}

function ProductRow({
  product: p,
  isOpen,
  onToggle,
}: {
  product: ProductSessionAgg;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const totalCarouselDwellMs =
    p.carousel_impressions_dwell_ms + p.carousel_sweep_dwell_ms;
  const hasRichDetail =
    p.detail_events.length > 0 ||
    p.feature_events.length > 0 ||
    p.gallery_events.length > 0;
  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-b border-border cursor-pointer hover:bg-soft/60 ${
          isOpen ? 'bg-soft/60' : ''
        }`}
      >
        <td className="py-1.5 truncate" style={{ maxWidth: '32ch' }}>
          {p.product_model}
        </td>
        <td className="py-1.5 pl-2 text-right tabular-nums">{p.impressions}</td>
        <td className="py-1.5 pl-2 text-right tabular-nums">{p.detail_views}</td>
        <td className="py-1.5 pl-2 text-right tabular-nums">
          {p.avg_detail_dwell_ms ? fmtMs(p.avg_detail_dwell_ms) : '—'}
        </td>
        <td className="py-1.5 pl-2 text-right tabular-nums">{p.popups}</td>
        <td className="py-1.5 pl-2 text-right tabular-nums">{p.gallery}</td>
        <td className="py-1.5 pl-2 text-right text-muted">
          {isOpen ? '▼' : '▶'}
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b border-border">
          <td colSpan={7} className="py-3 px-1 bg-soft/30">
            <div className="space-y-3">
              {p.detail_events.length > 0 && (
                <DetailBlock title="Detail views">
                  {p.detail_events.map((e, i) => (
                    <DetailRow
                      key={i}
                      offset={e.offset_ms}
                      tail={fmtMs(e.dwell_ms)}
                    />
                  ))}
                </DetailBlock>
              )}
              {p.feature_events.length > 0 && (
                <DetailBlock title="Popups">
                  {p.feature_events.map((e, i) => (
                    <DetailRow
                      key={i}
                      offset={e.offset_ms}
                      middle={e.pill_label || e.pill_id || '?'}
                      tail={fmtMs(e.dwell_ms)}
                    />
                  ))}
                </DetailBlock>
              )}
              {p.gallery_events.length > 0 && (
                <DetailBlock title="Gallery">
                  {p.gallery_events.map((e, i) => (
                    <DetailRow
                      key={i}
                      offset={e.offset_ms}
                      middle={`image #${e.gallery_index}`}
                      tail={fmtMs(e.dwell_ms)}
                    />
                  ))}
                </DetailBlock>
              )}
              {!hasRichDetail && (
                <p className="text-xs text-muted italic px-3">
                  Only seen briefly in the carousel.
                </p>
              )}
              {(p.impressions > 0 || p.sweeps > 0) && (
                <p className="text-[11px] text-muted px-3 pt-1 border-t border-border">
                  Carousel: {p.impressions} impression
                  {p.impressions === 1 ? '' : 's'}, {p.sweeps} sweep
                  {p.sweeps === 1 ? '' : 's'}
                  {totalCarouselDwellMs > 0 && (
                    <>
                      {' '}
                      ·{' '}
                      <span className="tabular-nums">
                        {fmtMs(totalCarouselDwellMs)}
                      </span>{' '}
                      total dwell
                    </>
                  )}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-3">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">
        {title}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function DetailRow({
  offset,
  middle,
  tail,
}: {
  offset: number;
  middle?: string;
  tail: string;
}) {
  return (
    <li className="flex items-baseline gap-3 text-xs">
      <span className="text-muted tabular-nums w-16 shrink-0">
        {fmtOffset(offset)}
      </span>
      {middle && (
        <span className="flex-1 truncate" title={middle}>
          {middle}
        </span>
      )}
      {!middle && <span className="flex-1" />}
      <span className="text-muted tabular-nums">{tail}</span>
    </li>
  );
}
